// MenuButton.tsx

import React from 'react';
import type {Item} from '@react-stately/collections';
import {useMenuTriggerState} from '@react-stately/menu';
import {useMenuTrigger} from '@react-aria/menu';
import {useButton} from '@react-aria/button';
import {mergeProps} from '@react-aria/utils';
import cx from 'classnames';

import type {AriaMenuProps} from './AriaMenu.types';
import {InnerAriaMenuNavigator} from './InnerAriaMenuNavigator';
import {InnerAriaMenuPopover} from './InnerAriaMenuPopover';

import styles from './AriaMenu.module.scss';

export const InnerAriaMenuButton: React.FC<
    Omit<AriaMenuProps, 'onOpenChange'> & {
        onOpenChange: (isOpen: boolean) => void;
    }
> = ({
    items,
    headerContent,
    button,
    renderButton,
    onOpenChange,
    testId,
    colorScheme,
    popoverPlacement,
    popoverOffset,
    menuClassName,
    itemClassName,
    backButtonLabel,
    onGoBack,
    showSubMenuIndicator,
}) => {
    const state = useMenuTriggerState({onOpenChange});

    const DEFAULT_OFFSET = 8;

    const ref = React.useRef(null);

    const {menuTriggerProps, menuProps} = useMenuTrigger<typeof Item>(
        {type: 'menu'},
        state,
        ref,
    );

    const {buttonProps} = useButton({...menuTriggerProps}, ref);

    const {
        onPress: _,
        onPressStart: __,
        preventFocusOnPress: ___,
        ...ariaButtonProps
    } = menuTriggerProps;

    const triggerButton = React.cloneElement(button, {
        ...mergeProps(buttonProps, ariaButtonProps),
    });

    return (
        <div className={cx(styles.innerMenuButton)}>
            <div ref={ref}>
                {renderButton ? renderButton(triggerButton) : triggerButton}
            </div>
            {state.isOpen && (
                <InnerAriaMenuPopover
                    state={state}
                    triggerRef={ref}
                    colorScheme={colorScheme}
                    placement={popoverPlacement ?? 'bottom start'}
                    offset={popoverOffset ?? DEFAULT_OFFSET}
                >
                    <InnerAriaMenuNavigator
                        rootItems={items}
                        headerContent={headerContent}
                        menuProps={menuProps}
                        close={() => state.close()}
                        testId={testId}
                        menuClassName={menuClassName}
                        itemClassName={itemClassName}
                        backButtonLabel={backButtonLabel}
                        onGoBack={onGoBack}
                        showSubMenuIndicator={showSubMenuIndicator}
                    />
                </InnerAriaMenuPopover>
            )}
        </div>
    );
};
